import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Trash2, ChevronDown, ChevronRight, ToggleLeft, ToggleRight, Loader2, Pencil } from 'lucide-react';
import { useAuth } from '@core/auth/useAuth';
import { emailSequenceService, EmailSequence, EmailSequenceStep } from '@core/supabase/services/emailSequenceService';
import { PageHeader } from '@shared/components/ui/PageHeader';
import { SyncStatusBadge } from '@shared/components/SyncStatusBadge';
import { FullPageSpinner } from '@shared/components/ui/Spinner';
import { ContextAiAssistModal, AiAssistIconButton } from '@shared/components/ai/ContextAiAssistModal';

const TRIGGER_OPTIONS = [
  { value: '', label: 'Manual only' },
  { value: 'application_submitted', label: 'Application submitted' },
  { value: 'interview_scheduled', label: 'Interview scheduled' },
  { value: 'offer_extended', label: 'Offer extended' },
  { value: 'candidate_placed', label: 'Candidate placed' },
  { value: 'no_response_7d', label: '7 days no response' },
];

function StepForm({
  orgId,
  onAdd,
}: {
  orgId: string;
  onAdd: (step: Omit<EmailSequenceStep, 'id' | 'sequence_id' | 'created_at'>) => void;
}) {
  const [form, setForm] = useState({ step_order: 1, delay_days: 0, template_key: '', subject: '', body_html: '' });
  const [bodyAiOpen, setBodyAiOpen] = useState(false);
  const bodyLooksHtml = /<[a-z][\s\S]*>/i.test(form.body_html);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="rounded-lg border vscode-border bg-gray-900/50 p-4 space-y-3">
      <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">New Step</p>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label">Step order</label>
          <input className="input" type="number" min={1} value={form.step_order} onChange={set('step_order')} />
        </div>
        <div>
          <label className="label">Delay (days)</label>
          <input className="input" type="number" min={0} value={form.delay_days} onChange={set('delay_days')} />
        </div>
        <div>
          <label className="label">Template key</label>
          <input className="input" placeholder="e.g. follow_up_1" value={form.template_key} onChange={set('template_key')} />
        </div>
      </div>
      <div>
        <label className="label">Subject line</label>
        <input className="input" placeholder="Following up on your application…" value={form.subject} onChange={set('subject')} />
      </div>
      <div>
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <label className="label mb-0">Body (HTML or plain text)</label>
          <AiAssistIconButton onClick={() => setBodyAiOpen(true)} title="AI assist — sequence email body" />
        </div>
        <textarea className="input min-h-[80px] font-mono text-xs" value={form.body_html} onChange={set('body_html')} placeholder="Hi {{candidate_name}},…" />
      </div>
      <button
        type="button"
        className="btn-primary text-sm"
        disabled={!form.subject.trim() || !form.body_html.trim()}
        onClick={() => {
          onAdd({ step_order: Number(form.step_order), delay_days: Number(form.delay_days), template_key: form.template_key, subject: form.subject, body_html: form.body_html });
          setForm({ step_order: form.step_order + 1, delay_days: 3, template_key: '', subject: '', body_html: '' });
        }}
      >
        <Plus className="w-3.5 h-3.5" /> [Add Step]
      </button>

      <ContextAiAssistModal
        open={bodyAiOpen}
        onClose={() => setBodyAiOpen(false)}
        orgId={orgId}
        feature="ai_assist_email_sequence_step_body"
        preset="email_sequence_body"
        title="AI — sequence step body"
        contextHint={`Subject: ${form.subject}\nStep order: ${form.step_order} · delay ${form.delay_days}d · template_key: ${form.template_key || '—'}`}
        original={form.body_html}
        originalFormat={bodyLooksHtml ? 'html' : 'plain'}
        onApply={(text, mode) =>
          setForm((f) => ({
            ...f,
            body_html:
              mode === 'replace'
                ? text
                : f.body_html.trim()
                  ? `${f.body_html.trim()}\n\n${text}`
                  : text,
          }))
        }
      />
    </div>
  );
}

function SequenceCard({
  seq,
  onToggle,
  onDelete,
  orgId,
}: {
  seq: EmailSequence;
  onToggle: (id: string, active: boolean) => void;
  onDelete: (id: string) => void;
  orgId: string;
}) {
  const [expanded, setExpanded] = useState(false);
  const [steps, setSteps] = useState<EmailSequenceStep[]>([]);
  const [loadingSteps, setLoadingSteps] = useState(false);
  const [showStepForm, setShowStepForm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadSteps = async () => {
    if (steps.length > 0) return;
    setLoadingSteps(true);
    try {
      const full = await emailSequenceService.get(seq.id);
      setSteps(full.steps);
    } finally {
      setLoadingSteps(false);
    }
  };

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) loadSteps();
  };

  const handleAddStep = async (stepData: Omit<EmailSequenceStep, 'id' | 'sequence_id' | 'created_at'>) => {
    const created = await emailSequenceService.addStep(seq.id, stepData);
    setSteps((prev) => [...prev, created].sort((a, b) => a.step_order - b.step_order));
  };

  const handleDeleteStep = async (stepId: string) => {
    await emailSequenceService.deleteStep(stepId);
    setSteps((prev) => prev.filter((s) => s.id !== stepId));
  };

  return (
    <div className="card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-medium vscode-text-primary">{seq.name}</p>
            <span className={`text-xs px-1.5 py-0.5 rounded ${seq.is_active ? 'bg-green-900/30 text-green-400' : 'vscode-text-tertiary bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_100%,transparent)]'}`}>
              {seq.is_active ? 'Active' : 'Inactive'}
            </span>
            {seq.trigger_event && (
              <span className="text-xs bg-brand-900/30 text-brand-300 px-1.5 py-0.5 rounded">
                {TRIGGER_OPTIONS.find((t) => t.value === seq.trigger_event)?.label ?? seq.trigger_event}
              </span>
            )}
          </div>
          {seq.description && <p className="text-sm vscode-text-tertiary mt-0.5">{seq.description}</p>}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <Link
            to={`/emails/sequences/${seq.id}`}
            className="p-1.5 vscode-text-secondary hover:vscode-text-primary"
            title="Open editor"
          >
            <Pencil className="w-4 h-4" />
          </Link>
          <button
            className="p-1.5 vscode-text-secondary hover:vscode-text-primary"
            title={seq.is_active ? 'Deactivate' : 'Activate'}
            onClick={() => onToggle(seq.id, !seq.is_active)}
          >
            {seq.is_active ? <ToggleRight className="w-4 h-4 text-green-400" /> : <ToggleLeft className="w-4 h-4" />}
          </button>
          <button
            className="p-1.5 vscode-text-secondary hover:text-red-400"
            disabled={deleting}
            onClick={async () => {
              if (!confirm('Delete this sequence and all its steps?')) return;
              setDeleting(true);
              await emailSequenceService.delete(seq.id);
              onDelete(seq.id);
            }}
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
          </button>
          <button className="p-1.5 vscode-text-secondary hover:vscode-text-primary" onClick={handleExpand}>
            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {expanded && (
        <div className="mt-4 border-t vscode-border pt-4 space-y-3">
          {loadingSteps ? (
            <div className="animate-pulse h-10 rounded bg-[color-mix(in_srgb,var(--vscode-bg-tertiary)_100%,transparent)]" />
          ) : steps.length === 0 ? (
            <p className="text-sm vscode-text-tertiary">No steps yet — add the first one below.</p>
          ) : (
            <ol className="space-y-2">
              {steps.map((s) => (
                <li key={s.id} className="flex items-start gap-3 rounded-lg border vscode-border bg-gray-900/40 p-3">
                  <span className="shrink-0 flex h-6 w-6 items-center justify-center rounded-full bg-brand-900/50 text-xs font-semibold text-brand-300">
                    {s.step_order}
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium vscode-text-primary">{s.subject}</p>
                    <p className="text-xs vscode-text-tertiary">
                      Send after {s.delay_days === 0 ? 'immediately' : `${s.delay_days} day${s.delay_days !== 1 ? 's' : ''}`}
                      {s.template_key ? ` · key: ${s.template_key}` : ''}
                    </p>
                  </div>
                  <button
                    className="vscode-text-tertiary hover:text-red-400 shrink-0"
                    onClick={() => handleDeleteStep(s.id)}
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </li>
              ))}
            </ol>
          )}

          {showStepForm ? (
            <StepForm orgId={orgId} onAdd={handleAddStep} />
          ) : (
            <button
              className="btn-secondary text-sm"
              onClick={() => setShowStepForm(true)}
            >
              <Plus className="w-3.5 h-3.5" /> [Add Step]
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function EmailSequencesPage() {
  const { user } = useAuth();
  const [sequences, setSequences] = useState<EmailSequence[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({ name: '', description: '', trigger_event: '' });

  useEffect(() => {
    if (!user?.orgId) return;
    emailSequenceService.list(user.orgId)
      .then(setSequences)
      .finally(() => setLoading(false));
  }, [user?.orgId]);

  const handleCreate = async () => {
    if (!user?.orgId || !newForm.name.trim()) return;
    setCreating(true);
    try {
      const created = await emailSequenceService.create(user.orgId, {
        name: newForm.name.trim(),
        description: newForm.description || undefined,
        trigger_event: newForm.trigger_event || undefined,
      });
      setSequences((prev) => [created, ...prev]);
      setNewForm({ name: '', description: '', trigger_event: '' });
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id: string, active: boolean) => {
    await emailSequenceService.update(id, { is_active: active });
    setSequences((prev) => prev.map((s) => s.id === id ? { ...s, is_active: active } : s));
  };

  const handleDelete = (id: string) =>
    setSequences((prev) => prev.filter((s) => s.id !== id));

  if (loading) return <FullPageSpinner />;

  return (
    <div className="space-y-5 max-w-3xl">
      <SyncStatusBadge className="mb-2" />
      <div className="mb-4 flex items-center gap-1 text-sm vscode-text-secondary">
        <Link to="/emails" className="hover:vscode-text-primary">Emails</Link>
        <span className="vscode-text-tertiary">/</span>
        <span className="vscode-text-primary">Sequences</span>
      </div>
      <PageHeader
        title="Email Sequences"
        subtitle="Multi-step drip campaigns. Each sequence runs independently or triggered by an application event."
      />

      {/* Create form */}
      <div className="card space-y-3">
        <p className="text-xs font-semibold vscode-text-secondary uppercase tracking-wider">New Sequence</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="label">Name *</label>
            <input
              className="input"
              placeholder="e.g. Post-submission nurture"
              value={newForm.name}
              onChange={(e) => setNewForm((f) => ({ ...f, name: e.target.value }))}
            />
          </div>
          <div>
            <label className="label">Trigger event</label>
            <select
              className="input"
              value={newForm.trigger_event}
              onChange={(e) => setNewForm((f) => ({ ...f, trigger_event: e.target.value }))}
            >
              {TRIGGER_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label className="label">Description</label>
          <input
            className="input"
            placeholder="Optional description of this sequence's purpose"
            value={newForm.description}
            onChange={(e) => setNewForm((f) => ({ ...f, description: e.target.value }))}
          />
        </div>
        <button
          className="btn-primary"
          disabled={!newForm.name.trim() || creating}
          onClick={handleCreate}
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          [Create Sequence]
        </button>
      </div>

      {/* List */}
      {sequences.length === 0 ? (
        <div className="card text-center py-10 vscode-text-tertiary text-sm">
          No sequences yet — create one above.
        </div>
      ) : (
        <div className="space-y-3">
          {sequences.map((seq) => (
            <SequenceCard
              key={seq.id}
              seq={seq}
              onToggle={handleToggle}
              onDelete={handleDelete}
              orgId={user?.orgId ?? ''}
            />
          ))}
        </div>
      )}
    </div>
  );
}
